import { useState, useRef, useEffect } from "react";
import { Layout } from "@/components/layout";
import { useAuth } from "@/contexts/auth";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Search, Upload, Edit, Trash2, MoreVertical, X, Image as ImageIcon,
  Camera, ChevronLeft, ChevronRight, FileJson, AlertTriangle, Check, BookOpen,
  Eye, Pencil, CheckCircle2, XCircle, Download, ChevronDown,
  Layers, FolderInput, Flag, CheckCheck, Languages, ShieldCheck, Settings,
} from "lucide-react";
import { QuestionReviewPanel } from "@/components/QuestionReviewPanel";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  type AllowedOptionCount,
  isValidImportQuestion,
  questionFormFromApi,
  serializeQuestionForm,
} from "@/lib/questionOptions";
import { CameraCapture } from "@/components/camera-capture";
import { ZoomableImage } from "@/components/zoomable-image";

const CATEGORIES = [
  "General Knowledge",
  "Air Law",
  "Meteorology",
  "Navigation",
];

const DIFFICULTIES = ["easy", "medium", "hard"];

interface Question {
  id: number;
  text: string;
  options: string[];
  correctOptionIndex: number;
  explanation: string;
  category: string;
  difficulty: string | null;
  imageUrl: string | null;
  explanationImageUrl: string | null;
  createdAt: string;
  openFlagCount?: number;
  translatedAt?: string | null;
  textFr?: string | null;
  optionsFr?: string[] | null;
  explanationFr?: string | null;
  referenceUrl?: string | null;
  publishStatus?: string;
  reviewApprovals?: number;
  reviewRejections?: number;
  reviews?: { id: number; name: string; email: string; decision: string }[];
}

interface QuestionsResponse {
  total: number;
  page: number;
  limit: number;
  questions: Question[];
  reviewSettings?: { enabled: boolean; reviewersRequired: 1 | 2 };
}

interface QuestionForm {
  text: string;
  options: [string, string, string, string];
  optionCount: AllowedOptionCount;
  correctOptionIndex: number;
  explanation: string;
  category: string;
  difficulty: string;
  imageUrl: string | null;
  explanationImageUrl: string | null;
  textFr: string;
  optionsFr: [string, string, string, string];
  explanationFr: string;
  referenceUrl: string;
}

const BLANK_FORM: QuestionForm = {
  text: "",
  options: ["", "", "", ""],
  optionCount: 4,
  correctOptionIndex: 0,
  explanation: "",
  category: CATEGORIES[0],
  difficulty: "medium",
  imageUrl: null,
  explanationImageUrl: null,
  textFr: "",
  optionsFr: ["", "", "", ""],
  explanationFr: "",
  referenceUrl: "",
};

function getBaseUrl() {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

async function fetchWithAuth(path: string, options?: RequestInit) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

async function uploadImage(imageData: string): Promise<string> {
  const result = await fetchWithAuth("/api/admin/upload-image", {
    method: "POST",
    body: JSON.stringify({ imageData }),
  });
  return result.url as string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageUploader({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (url: string | null) => void;
}) {
  const galleryRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const { toast } = useToast();

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const url = await uploadImage(base64);
      onChange(`${getBaseUrl()}${url}`);
    } catch (err) {
      console.warn("[admin-questions] Image upload failed:", err);
      toast({ title: "Upload failed", description: "Could not upload image.", variant: "destructive" });
    } finally {
      setUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const clearImage = () => {
    onChange(null);
    if (galleryRef.current) galleryRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-muted-foreground">{label} (optional)</label>
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
      {cameraOpen && (
        <CameraCapture
          onCapture={handleFile}
          onClose={() => setCameraOpen(false)}
        />
      )}
      {value ? (
        <div className="relative group border border-border rounded-md overflow-hidden bg-secondary/20">
          <img src={value} alt="Question visual" className="max-h-48 w-full object-contain p-2" />
          <button
            type="button"
            onClick={clearImage}
            className="absolute top-2 right-2 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : uploading ? (
        <div className="flex items-center justify-center gap-2 w-full h-20 border-2 border-dashed border-border rounded-md text-muted-foreground text-sm">
          <span className="font-mono text-xs animate-pulse">UPLOADING...</span>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            className="flex flex-col items-center justify-center gap-1.5 flex-1 h-20 border-2 border-dashed border-border rounded-md text-muted-foreground hover:border-primary/50 hover:text-primary active:bg-primary/5 transition-colors"
          >
            <Camera className="w-5 h-5" />
            <span className="text-xs font-mono">CAMERA</span>
          </button>
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            className="flex flex-col items-center justify-center gap-1.5 flex-1 h-20 border-2 border-dashed border-border rounded-md text-muted-foreground hover:border-primary/50 hover:text-primary active:bg-primary/5 transition-colors"
          >
            <ImageIcon className="w-5 h-5" />
            <span className="text-xs font-mono">GALLERY / FILES</span>
          </button>
        </div>
      )}
    </div>
  );
}

function QuestionPreview({ form, effectiveCategory }: { form: QuestionForm; effectiveCategory: string }) {
  const hasContent = form.text.trim() || form.options.some(o => o.trim());
  if (!hasContent) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/40 gap-3">
        <Eye className="w-10 h-10" />
        <p className="text-sm font-mono">Start filling in the form to see a preview</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-2">
      {/* Category label */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-mono text-muted-foreground tracking-wider uppercase">
          {effectiveCategory || "No category"}
        </span>
        <span className="text-xs font-mono text-muted-foreground/40">PREVIEW</span>
      </div>

      {/* Question text */}
      <h2 className="text-xl font-bold leading-relaxed text-foreground">
        {form.text || <span className="text-muted-foreground/40 italic">No question text yet...</span>}
      </h2>

      {/* Question image */}
      {form.imageUrl && (
        <ZoomableImage
          src={form.imageUrl}
          alt="Question diagram"
          thumbnailClass="max-h-64"
        />
      )}

      {/* Answer options */}
      <div className="grid grid-cols-1 gap-2.5">
        {form.options.slice(0, form.optionCount).map((opt, i) => {
          const isCorrect = i === form.correctOptionIndex;
          const hasText = opt.trim();
          return (
            <div
              key={i}
              className={cn(
                "flex items-center gap-4 p-4 rounded-lg border-2 transition-colors",
                isCorrect
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card/50 text-muted-foreground opacity-70"
              )}
            >
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-bold font-mono shrink-0",
                isCorrect ? "border-primary text-primary" : "border-muted-foreground text-muted-foreground"
              )}>
                {String.fromCharCode(65 + i)}
              </div>
              <span className="flex-1 text-base">
                {hasText ? opt : <span className="text-muted-foreground/40 italic text-sm">Empty answer</span>}
              </span>
              {isCorrect && <CheckCircle2 className="w-5 h-5 shrink-0" />}
            </div>
          );
        })}
      </div>

      {/* Explanation card */}
      <div className={cn(
        "rounded-lg border p-5 space-y-3 bg-card/50",
        "border-primary/40"
      )}>
        <h3 className="text-base font-bold flex items-center gap-2 text-primary">
          <CheckCircle2 className="w-5 h-5" />
          EXPLANATION (shown after answering)
        </h3>
        <p className="text-muted-foreground leading-relaxed text-sm">
          {form.explanation.trim() || <span className="italic text-muted-foreground/40">No explanation entered yet.</span>}
        </p>
        {form.explanationImageUrl && (
          <ZoomableImage
            src={form.explanationImageUrl}
            alt="Explanation diagram"
            thumbnailClass="max-h-48"
          />
        )}
      </div>
    </div>
  );
}

function QuestionModal({
  initial,
  onClose,
  onSave,
  saving,
  categories,
}: {
  initial: QuestionForm | null;
  onClose: () => void;
  onSave: (form: QuestionForm) => void;
  saving: boolean;
  categories: string[];
}) {
  const [form, setForm] = useState<QuestionForm>(initial ?? BLANK_FORM);
  const [customCat, setCustomCat] = useState(!CATEGORIES.includes(form.category) ? form.category : "");
  const [useCustomCat, setUseCustomCat] = useState(!CATEGORIES.includes(form.category));
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const [editLang, setEditLang] = useState<"en" | "fr">("en");

  const set = (k: keyof QuestionForm, v: QuestionForm[keyof QuestionForm]) => setForm(f => ({ ...f, [k]: v }));
  const setOption = (i: number, v: string) => setForm(f => {
    const opts = [...f.options] as [string, string, string, string];
    opts[i] = v;
    return { ...f, options: opts };
  });
  const setOptionFr = (i: number, v: string) => setForm(f => {
    const opts = [...f.optionsFr] as [string, string, string, string];
    opts[i] = v;
    return { ...f, optionsFr: opts };
  });

  const setOptionMode = (count: AllowedOptionCount) => {
    setForm(f => {
      if (count === 2) {
        return {
          ...f,
          optionCount: 2,
          options: [f.options[0]?.trim() || "True", f.options[1]?.trim() || "False", f.options[2] ?? "", f.options[3] ?? ""],
          optionsFr: [f.optionsFr[0]?.trim() || "Vrai", f.optionsFr[1]?.trim() || "Faux", f.optionsFr[2] ?? "", f.optionsFr[3] ?? ""],
          correctOptionIndex: Math.min(f.correctOptionIndex, 1),
        };
      }
      return { ...f, optionCount: 4, correctOptionIndex: Math.min(f.correctOptionIndex, 3) };
    });
  };

  const effectiveCategory = useCustomCat ? customCat : form.category;
  const activeOptions = form.options.slice(0, form.optionCount);
  const activeOptionsFr = form.optionsFr.slice(0, form.optionCount);
  const canSave =
    form.text.trim() &&
    activeOptions.every(o => o.trim()) &&
    form.explanation.trim() &&
    effectiveCategory.trim();

  const handleSubmit = () => {
    if (!canSave) return;
    onSave({ ...form, category: effectiveCategory });
  };

  const frComplete = !!(
    form.textFr.trim() &&
    activeOptionsFr.every(o => o.trim()) &&
    form.explanationFr.trim()
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border rounded-lg w-full max-w-2xl my-8 shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-primary" />
            <h2 className="font-bold text-foreground font-mono tracking-wide">
              {initial ? "EDIT QUESTION" : "ADD QUESTION"}
            </h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Top tab bar: EDIT | PREVIEW */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setTab("edit")}
            className={cn(
              "flex items-center gap-2 px-5 py-3 text-sm font-mono tracking-wide transition-colors border-b-2 -mb-px",
              tab === "edit"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Pencil className="w-3.5 h-3.5" />
            EDIT
          </button>
          <button
            onClick={() => setTab("preview")}
            className={cn(
              "flex items-center gap-2 px-5 py-3 text-sm font-mono tracking-wide transition-colors border-b-2 -mb-px",
              tab === "preview"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Eye className="w-3.5 h-3.5" />
            PREVIEW
          </button>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[65vh] overflow-y-auto">
          {tab === "preview" ? (
            <QuestionPreview form={form} effectiveCategory={effectiveCategory} />
          ) : (
            <div className="space-y-5">

              {/* Language switcher */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditLang("en")}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono font-semibold tracking-wide border transition-colors",
                    editLang === "en"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:text-foreground"
                  )}
                >
                  🇨🇦 EN
                </button>
                <button
                  onClick={() => setEditLang("fr")}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono font-semibold tracking-wide border transition-colors",
                    editLang === "fr"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:text-foreground"
                  )}
                >
                  🇫🇷 FR
                  {frComplete
                    ? <span className="ml-1 w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                    : <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                  }
                </button>
                {editLang === "fr" && (
                  <span className="text-xs text-muted-foreground ml-1">
                    {frComplete ? "French content complete" : "French content incomplete — will be auto-translated if left empty"}
                  </span>
                )}
              </div>

              {editLang === "en" ? (
                <>
                  {/* Question text EN */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Question Text <span className="text-destructive">*</span></label>
                    <Textarea
                      value={form.text}
                      onChange={e => set("text", e.target.value)}
                      placeholder="Enter the question..."
                      className="min-h-[80px] bg-background resize-none"
                    />
                  </div>

                  {/* Question image */}
                  <ImageUploader
                    label="Question Image"
                    value={form.imageUrl}
                    onChange={url => set("imageUrl", url)}
                  />

                  {/* Answers EN */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Answer Options <span className="text-destructive">*</span></label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={form.optionCount === 4 ? "default" : "outline"}
                        onClick={() => setOptionMode(4)}
                      >
                        4 choices (MCQ)
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={form.optionCount === 2 ? "default" : "outline"}
                        onClick={() => setOptionMode(2)}
                      >
                        True / False
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Select the radio button next to the correct answer.</p>
                    {form.options.slice(0, form.optionCount).map((opt, i) => (
                      <div key={i} className={cn(
                        "flex items-center gap-3 p-3 rounded-md border transition-colors",
                        form.correctOptionIndex === i ? "border-primary bg-primary/5" : "border-border bg-background"
                      )}>
                        <input
                          type="radio"
                          name="correct"
                          checked={form.correctOptionIndex === i}
                          onChange={() => set("correctOptionIndex", i)}
                          className="accent-primary w-4 h-4 shrink-0"
                        />
                        <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">{String.fromCharCode(65 + i)}.</span>
                        <Input
                          value={opt}
                          onChange={e => setOption(i, e.target.value)}
                          placeholder={`Answer ${String.fromCharCode(65 + i)}`}
                          className={cn("flex-1 bg-transparent border-0 shadow-none focus-visible:ring-0 px-0 text-sm", form.correctOptionIndex === i && "font-medium")}
                        />
                        {form.correctOptionIndex === i && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </div>
                    ))}
                  </div>

                  {/* Explanation EN */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Explanation <span className="text-destructive">*</span></label>
                    <Textarea
                      value={form.explanation}
                      onChange={e => set("explanation", e.target.value)}
                      placeholder="Explain why the correct answer is correct..."
                      className="min-h-[80px] bg-background resize-none"
                    />
                  </div>

                  {/* Explanation image */}
                  <ImageUploader
                    label="Explanation Image"
                    value={form.explanationImageUrl}
                    onChange={url => set("explanationImageUrl", url)}
                  />

                  {/* Reference URL */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reference URL <span className="text-muted-foreground text-xs font-normal">(optional — shown to students after answering)</span></label>
                    <Input
                      type="url"
                      value={form.referenceUrl}
                      onChange={e => set("referenceUrl", e.target.value)}
                      placeholder="https://tc.canada.ca/en/..."
                      className="bg-background"
                    />
                  </div>

                  {/* Category + Difficulty */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Section / Category <span className="text-destructive">*</span></label>
                      <select
                        value={useCustomCat ? "__custom__" : form.category}
                        onChange={e => {
                          if (e.target.value === "__custom__") {
                            setUseCustomCat(true);
                          } else {
                            setUseCustomCat(false);
                            setCustomCat("");
                            set("category", e.target.value);
                          }
                        }}
                        className="w-full h-9 rounded-md border border-border bg-background px-3 py-1 text-sm"
                      >
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        <option value="__custom__">+ Custom category...</option>
                      </select>
                      {useCustomCat && (
                        <Input
                          value={customCat}
                          onChange={e => setCustomCat(e.target.value)}
                          placeholder="Enter category name"
                          className="bg-background"
                          autoFocus
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Difficulty</label>
                      <select
                        value={form.difficulty}
                        onChange={e => set("difficulty", e.target.value)}
                        className="w-full h-9 rounded-md border border-border bg-background px-3 py-1 text-sm"
                      >
                        {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* FR note */}
                  <div className="rounded-md bg-blue-500/10 border border-blue-500/20 px-3 py-2.5 text-xs text-blue-300 space-y-1">
                    <p className="font-semibold">French translation</p>
                    <p>Correct answer is <span className="font-bold">{String.fromCharCode(65 + form.correctOptionIndex)}</span> — options must stay in the same order as English. Category and difficulty are shared.</p>
                    <p className="text-blue-400/70">If left empty, French fields stay blank until you paste translations or run your own translation workflow.</p>
                  </div>

                  {/* Question text FR */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Question Text (FR)</label>
                    <Textarea
                      value={form.textFr}
                      onChange={e => set("textFr", e.target.value)}
                      placeholder="Saisissez la question en français..."
                      className="min-h-[80px] bg-background resize-none"
                      lang="fr"
                    />
                    {form.text && (
                      <p className="text-xs text-muted-foreground/60 italic">EN: {form.text}</p>
                    )}
                  </div>

                  {/* Answers FR */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium">Answer Options (FR)</label>
                    {form.optionsFr.slice(0, form.optionCount).map((opt, i) => (
                      <div key={i} className={cn(
                        "flex items-center gap-3 p-3 rounded-md border transition-colors",
                        form.correctOptionIndex === i ? "border-primary bg-primary/5" : "border-border bg-background"
                      )}>
                        <span className={cn(
                          "w-4 h-4 rounded-full border-2 shrink-0",
                          form.correctOptionIndex === i ? "border-primary bg-primary" : "border-muted-foreground/30"
                        )} />
                        <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">{String.fromCharCode(65 + i)}.</span>
                        <Input
                          value={opt}
                          onChange={e => setOptionFr(i, e.target.value)}
                          placeholder={form.options[i] ? `FR: ${form.options[i]}` : `Réponse ${String.fromCharCode(65 + i)}`}
                          className={cn("flex-1 bg-transparent border-0 shadow-none focus-visible:ring-0 px-0 text-sm", form.correctOptionIndex === i && "font-medium")}
                          lang="fr"
                        />
                        {form.correctOptionIndex === i && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </div>
                    ))}
                  </div>

                  {/* Explanation FR */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Explanation (FR)</label>
                    <Textarea
                      value={form.explanationFr}
                      onChange={e => set("explanationFr", e.target.value)}
                      placeholder="Expliquez pourquoi la bonne réponse est correcte..."
                      className="min-h-[80px] bg-background resize-none"
                      lang="fr"
                    />
                    {form.explanation && (
                      <p className="text-xs text-muted-foreground/60 italic">EN: {form.explanation}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTab(tab === "edit" ? "preview" : "edit")}
            className="text-muted-foreground font-mono text-xs gap-2"
          >
            {tab === "edit" ? <><Eye className="w-3.5 h-3.5" />Preview</> : <><Pencil className="w-3.5 h-3.5" />Back to Edit</>}
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!canSave || saving}>
              {saving ? "Saving..." : initial ? "Save Changes" : "Add Question"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const IMPORT_FORMATS = {
  json: {
    label: "JSON",
    ext: ".json",
    accept: ".json,application/json",
    example: `[
  {
    "text": "What is the minimum age to obtain a CPL in Canada?",
    "options": ["17", "18", "19", "21"],
    "correctOptionIndex": 1,
    "explanation": "CARs 401.05 requires applicants to be at least 18 years of age.",
    "category": "Air Law",
    "difficulty": "easy",
    "referenceUrl": "https://lois-laws.justice.gc.ca/eng/regulations/SOR-96-433/index.html",
    "text_fr": "Quel est l'âge minimum pour obtenir une LPC au Canada?",
    "options_fr": ["17", "18", "19", "21"],
    "explanation_fr": "Le RAC 401.05 exige que les demandeurs aient au moins 18 ans."
  }
]`,
    parse: (text: string): unknown[] => {
      const parsed = JSON.parse(text);
      const qs = Array.isArray(parsed) ? parsed : parsed.questions;
      if (!Array.isArray(qs)) throw new Error("JSON must be an array or { questions: [] }");
      return qs;
    },
  },
  csv: {
    label: "CSV",
    ext: ".csv",
    accept: ".csv,text/csv",
    example: `text,option_a,option_b,option_c,option_d,correct_option_index,explanation,category,difficulty,reference_url,text_fr,option_a_fr,option_b_fr,option_c_fr,option_d_fr,explanation_fr
What is the minimum age to obtain a CPL in Canada?,17,18,19,21,1,CARs 401.05 requires applicants to be at least 18 years of age.,Air Law,easy,https://lois-laws.justice.gc.ca/eng/regulations/SOR-96-433/index.html,Quel est l'âge minimum pour obtenir une LPC au Canada?,17,18,19,21,Le RAC 401.05 exige que les demandeurs aient au moins 18 ans.`,
    parse: (text: string): unknown[] => {
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) throw new Error("CSV must have a header row and at least one data row");
      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      const parseCsvLine = (line: string): string[] => {
        const out: string[] = [];
        let cur = "", inQ = false;
        for (let i = 0; i < line.length; i++) {
          const c = line[i];
          if (c === '"' && !inQ) { inQ = true; continue; }
          if (c === '"' && inQ && line[i + 1] === '"') { cur += '"'; i++; continue; }
          if (c === '"' && inQ) { inQ = false; continue; }
          if (c === ',' && !inQ) { out.push(cur); cur = ""; continue; }
          cur += c;
        }
        out.push(cur);
        return out;
      };
      return lines.slice(1).map(line => {
        const vals = parseCsvLine(line);
        const get = (key: string) => vals[headers.indexOf(key)] ?? "";
        const frA = get("option_a_fr"), frB = get("option_b_fr"), frC = get("option_c_fr"), frD = get("option_d_fr");
        const hasFr = [get("text_fr"), frA, frB, frC, frD, get("explanation_fr")].every(v => v.trim());
        const ref = get("reference_url").trim();
        return {
          text: get("text"),
          options: [get("option_a"), get("option_b"), get("option_c"), get("option_d")],
          correctOptionIndex: parseInt(get("correct_option_index"), 10),
          explanation: get("explanation"),
          category: get("category"),
          difficulty: get("difficulty") || null,
          ...(ref ? { referenceUrl: ref } : {}),
          ...(hasFr ? {
            text_fr: get("text_fr"),
            options_fr: [frA, frB, frC, frD],
            explanation_fr: get("explanation_fr"),
          } : {}),
        };
      });
    },
  },
  tsv: {
    label: "TSV",
    ext: ".tsv",
    accept: ".tsv,text/tab-separated-values",
    example: `text\toption_a\toption_b\toption_c\toption_d\tcorrect_option_index\texplanation\tcategory\tdifficulty\treference_url\ttext_fr\toption_a_fr\toption_b_fr\toption_c_fr\toption_d_fr\texplanation_fr
What is the minimum age to obtain a CPL in Canada?\t17\t18\t19\t21\t1\tCARs 401.05 requires applicants to be at least 18 years of age.\tAir Law\teasy\thttps://lois-laws.justice.gc.ca/eng/regulations/SOR-96-433/index.html\tQuel est l'âge minimum pour obtenir une LPC au Canada?\t17\t18\t19\t21\tLe RAC 401.05 exige que les demandeurs aient au moins 18 ans.`,
    parse: (text: string): unknown[] => {
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) throw new Error("TSV must have a header row and at least one data row");
      const headers = lines[0].split("\t").map(h => h.trim());
      return lines.slice(1).map(line => {
        const vals = line.split("\t");
        const get = (key: string) => vals[headers.indexOf(key)]?.trim() ?? "";
        const frA = get("option_a_fr"), frB = get("option_b_fr"), frC = get("option_c_fr"), frD = get("option_d_fr");
        const hasFr = [get("text_fr"), frA, frB, frC, frD, get("explanation_fr")].every(v => v.trim());
        const ref = get("reference_url").trim();
        return {
          text: get("text"),
          options: [get("option_a"), get("option_b"), get("option_c"), get("option_d")],
          correctOptionIndex: parseInt(get("correct_option_index"), 10),
          explanation: get("explanation"),
          category: get("category"),
          difficulty: get("difficulty") || null,
          ...(ref ? { referenceUrl: ref } : {}),
          ...(hasFr ? {
            text_fr: get("text_fr"),
            options_fr: [frA, frB, frC, frD],
            explanation_fr: get("explanation_fr"),
          } : {}),
        };
      });
    },
  },
} as const;

type ImportFormat = keyof typeof IMPORT_FORMATS;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
    >
      {copied ? <><Check className="w-3 h-3 text-primary" />Copied!</> : <><CheckCircle2 className="w-3 h-3" />Copy example</>}
    </button>
  );
}

function downloadSampleJson() {
  const sample = [
    {
      text: "What is the minimum age to obtain a CPL in Canada?",
      options: ["17", "18", "19", "21"],
      correctOptionIndex: 1,
      explanation: "CARs 401.05 requires applicants to be at least 18 years of age.",
      category: "Air Law",
      difficulty: "easy",
      referenceUrl: "https://lois-laws.justice.gc.ca/eng/regulations/SOR-96-433/index.html",
      text_fr: "Quel est l'âge minimum pour obtenir une licence de pilote professionnel au Canada?",
      options_fr: ["17", "18", "19", "21"],
      explanation_fr: "Le RAC 401.05 exige que les demandeurs aient au moins 18 ans.",
    },
    {
      text: "Under CARs, what is the required minimum dual flight time for a CPL applicant?",
      options: ["65 hours", "70 hours", "75 hours", "100 hours"],
      correctOptionIndex: 1,
      explanation: "CARs 401.19 requires a minimum of 70 hours of dual flight instruction.",
      category: "Air Law",
      difficulty: "medium",
      referenceUrl: "https://lois-laws.justice.gc.ca/eng/regulations/SOR-96-433/index.html",
      text_fr: "Selon le RAC, quel est le temps de vol en double commande minimum requis pour un candidat à la LPC?",
      options_fr: ["65 heures", "70 heures", "75 heures", "100 heures"],
      explanation_fr: "Le RAC 401.19 exige un minimum de 70 heures d'instruction en double commande.",
    },
    {
      text: "What does METAR stand for?",
      options: [
        "Meteorological Aerodrome Report",
        "Meteorological Aerodrome Record",
        "Meteorological Aviation Terminal Report",
        "Meteorological Automated Terminal Report",
      ],
      correctOptionIndex: 0,
      explanation: "METAR stands for Meteorological Aerodrome Report — a routine surface weather observation.",
      category: "Meteorology",
      difficulty: "easy",
      text_fr: "Que signifie l'acronyme METAR?",
      options_fr: [
        "Rapport météorologique d'aérodrome",
        "Registre météorologique d'aérodrome",
        "Rapport météorologique de terminal d'aviation",
        "Rapport automatisé de terminal météorologique",
      ],
      explanation_fr: "METAR signifie Meteorological Aerodrome Report — une observation météorologique de surface de routine.",
    },
  ];
  const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "flightdeck_import_sample_bilingual.json";
  a.click();
  URL.revokeObjectURL(url);
}

function ImportModal({
  onClose,
  onImport,
  importing,
}: {
  onClose: () => void;
  onImport: (questions: Record<string, unknown>[]) => void;
  importing: boolean;
}) {
  const [format, setFormat] = useState<ImportFormat>("json");
  const [preview, setPreview] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const fmt = IMPORT_FORMATS[format];

  const handleFile = (file: File) => {
    setError(null);
    setPreview(null);
    const ext = file.name.split(".").pop()?.toLowerCase() as ImportFormat | undefined;
    const detectedFormat = (ext && ext in IMPORT_FORMATS) ? ext as ImportFormat : format;
    if (detectedFormat !== format) setFormat(detectedFormat);
    const parser = IMPORT_FORMATS[detectedFormat].parse;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setPreview(parser(reader.result as string));
      } catch (e: unknown) {
        setError((e instanceof Error ? e.message : undefined) || `Invalid ${detectedFormat.toUpperCase()} file`);
      }
    };
    reader.readAsText(file);
  };

  const validCount = preview?.filter(isValidImportQuestion).length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border rounded-lg w-full max-w-xl shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-primary" />
            <h2 className="font-bold text-foreground font-mono tracking-wide">IMPORT QUESTIONS</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Format tabs */}
          <div className="flex gap-1 bg-secondary/40 p-1 rounded-lg">
            {(Object.keys(IMPORT_FORMATS) as ImportFormat[]).map(f => (
              <button
                key={f}
                onClick={() => { setFormat(f); setPreview(null); setError(null); }}
                className={cn(
                  "flex-1 py-1.5 rounded-md text-xs font-mono font-semibold tracking-wider transition-colors",
                  format === f ? "bg-card text-primary shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {IMPORT_FORMATS[f].label}
              </button>
            ))}
          </div>

          {/* Bilingual info note */}
          <div className="flex items-start gap-2 p-3 bg-primary/5 border border-primary/20 rounded-md text-[11px] font-mono text-muted-foreground">
            <span className="text-primary shrink-0 mt-0.5">FR</span>
            <span>Include <strong className="text-foreground">text_fr / options_fr / explanation_fr</strong> to import both languages at once — no auto-translate will be triggered for those questions. Optional: <strong className="text-foreground">referenceUrl</strong> (JSON) or <strong className="text-foreground">reference_url</strong> (CSV/TSV) for the official link shown after answer reveal.</span>
          </div>

          {/* Example block */}
          <div className="bg-secondary/40 rounded-md border border-border overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
              <span className="text-[11px] font-mono font-semibold text-foreground">
                Expected {fmt.label} format:
              </span>
              <div className="flex items-center gap-2">
                {format === "json" && (
                  <button
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    onClick={downloadSampleJson}
                  >
                    <Download className="w-3 h-3" />
                    Download sample
                  </button>
                )}
                <CopyButton text={fmt.example} />
              </div>
            </div>
            <pre className="overflow-x-auto text-[10px] font-mono text-muted-foreground p-3 leading-relaxed max-h-36 overflow-y-auto">
              {fmt.example}
            </pre>
          </div>

          {/* File input */}
          <input
            ref={fileRef}
            type="file"
            accept={fmt.accept}
            className="hidden"
            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center justify-center gap-2 w-full h-20 border-2 border-dashed border-border rounded-md text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
          >
            <Upload className="w-5 h-5" />
            <span className="text-sm">Click to select a <strong>{fmt.label}</strong> file, or drag and drop</span>
          </button>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {preview && (
            <div className="space-y-2">
              <p className="text-sm font-mono">
                Found <span className="text-primary font-bold">{preview.length}</span> records —{" "}
                <span className="text-primary font-bold">{validCount}</span> valid,{" "}
                <span className={validCount < preview.length ? "text-destructive" : "text-muted-foreground"}>
                  {preview.length - validCount} will be skipped
                </span>
              </p>
              <div className="max-h-36 overflow-y-auto space-y-1 border border-border rounded-md p-2 bg-secondary/20">
                {preview.slice(0, 6).map((q, i) => (
                  <div key={i} className={cn("text-xs font-mono truncate", typeof q.text === "string" && q.text ? "text-muted-foreground" : "text-destructive/70")}>
                    {i + 1}. {q.text?.slice(0, 80) || "(missing text — will be skipped)"}
                  </div>
                ))}
                {preview.length > 6 && <div className="text-xs text-muted-foreground font-mono">...and {preview.length - 6} more</div>}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={importing}>Cancel</Button>
            <Button
              className="flex-1"
              disabled={!preview || validCount === 0 || importing}
              onClick={() => preview && onImport(preview)}
            >
              {importing ? "Importing..." : `Import ${validCount} Questions`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkMoveModal({
  count,
  allCategories,
  currentTrack,
  onClose,
  onApply,
  applying,
}: {
  count: number;
  allCategories: string[];
  currentTrack: string;
  onClose: () => void;
  onApply: (category: string | null, track: string | null) => void;
  applying: boolean;
}) {
  const [targetCategory, setTargetCategory] = useState<string>("");
  const [targetTrack, setTargetTrack] = useState<string>("");
  const [customCat, setCustomCat] = useState("");
  const [useCustomCat, setUseCustomCat] = useState(false);

  const TRACKS = [
    { key: "ppl", label: "PPL", color: "border-blue-400/60 text-blue-400 hover:bg-blue-500/10" },
    { key: "cpl", label: "CPL", color: "border-primary/60 text-primary hover:bg-primary/10" },
    { key: "pstar", label: "PSTAR", color: "border-sky-400/60 text-sky-400 hover:bg-sky-500/10" },
    { key: "roca", label: "ROC-A", color: "border-amber-400/60 text-amber-400 hover:bg-amber-500/10" },
  ];

  const effectiveCat = useCustomCat ? customCat.trim() : targetCategory;
  const canApply = (effectiveCat.length > 0 || targetTrack.length > 0) && !applying;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border rounded-lg w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <FolderInput className="w-5 h-5 text-primary" />
            <h2 className="font-bold text-foreground font-mono tracking-wide">MOVE {count} QUESTION{count !== 1 ? "S" : ""}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">Change Category</label>
            <select
              value={useCustomCat ? "__custom__" : targetCategory}
              onChange={e => {
                if (e.target.value === "__custom__") { setUseCustomCat(true); }
                else { setUseCustomCat(false); setCustomCat(""); setTargetCategory(e.target.value); }
              }}
              className="w-full h-9 rounded-md border border-border bg-background px-3 py-1 text-sm"
            >
              <option value="">— Keep current category —</option>
              {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="__custom__">+ Custom category...</option>
            </select>
            {useCustomCat && (
              <Input value={customCat} onChange={e => setCustomCat(e.target.value)} placeholder="Enter category name" className="bg-background" autoFocus />
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Move to Track</label>
            <div className="grid grid-cols-4 gap-2">
              {TRACKS.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTargetTrack(prev => prev === t.key ? "" : t.key)}
                  className={cn(
                    "px-3 py-2 rounded-md border text-sm font-mono font-bold transition-colors",
                    targetTrack === t.key
                      ? t.color + " border-opacity-100"
                      : "border-border text-muted-foreground hover:border-muted-foreground/40",
                    t.key === currentTrack && targetTrack !== t.key && "opacity-50"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {targetTrack && targetTrack === currentTrack && (
              <p className="text-xs text-muted-foreground font-mono">Already in this track — only category will change.</p>
            )}
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-border">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button className="flex-1" disabled={!canApply} onClick={() => onApply(effectiveCat || null, targetTrack || null)}>
            {applying ? "Moving..." : `Apply to ${count} Question${count !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ActionMenu({
  onEdit,
  onDelete,
  onTranslate,
}: {
  onEdit: () => void;
  onDelete: () => void;
  onTranslate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-card border border-border rounded-md shadow-lg min-w-[160px] py-1">
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
          >
            <Edit className="w-3.5 h-3.5" />
            Edit
          </button>
          {onTranslate && (
            <button
              onClick={() => { setOpen(false); onTranslate(); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-blue-400 hover:bg-blue-500/10 transition-colors"
            >
              <Languages className="w-3.5 h-3.5" />
              Translate FR
            </button>
          )}
          <button
            onClick={() => { setOpen(false); onDelete(); }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminQuestions() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  type Track = "ppl" | "cpl" | "pstar" | "roca";
  const TRACKS: { key: Track; label: string; fullName: string; color: string; activeClass: string }[] = [
    { key: "ppl", label: "PPL", fullName: "Private Pilot Licence", color: "text-blue-400", activeClass: "border-blue-400 text-blue-400" },
    { key: "cpl", label: "CPL", fullName: "Commercial Pilot Licence", color: "text-primary", activeClass: "border-primary text-primary" },
    { key: "pstar", label: "PSTAR", fullName: "Student Pilot (PSTAR)", color: "text-sky-400", activeClass: "border-sky-400 text-sky-400" },
    { key: "roca", label: "ROC-A", fullName: "Radio Operator Certificate — Aviation", color: "text-amber-400", activeClass: "border-amber-400 text-amber-400" },
  ];

  const BANK_COUNT_KEYS: Track[] = ["ppl", "cpl", "pstar", "roca"];

  const [activeTrack, setActiveTrack] = useState<Track>("cpl");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [imageFilter, setImageFilter] = useState<"" | "yes" | "no">("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [flaggedFilter, setFlaggedFilter] = useState("");
  const [translatedFilter, setTranslatedFilter] = useState("");
  const [idMin, setIdMin] = useState("");
  const [idMax, setIdMax] = useState("");
  const [flagModal, setFlagModal] = useState<{ questionId: number; questionText: string } | null>(null);
  const [flagDetails, setFlagDetails] = useState<any[]>([]);
  const [flagDetailsLoading, setFlagDetailsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [addModal, setAddModal] = useState(false);
  const [editQuestion, setEditQuestion] = useState<Question | null>(null);
  const [deleteQuestion, setDeleteQuestion] = useState<Question | null>(null);
  const [importModal, setImportModal] = useState(false);
  const isModerator = user?.role === "moderator";
  const isMaster = user?.role === "master_admin";
  const [pageView, setPageView] = useState<"bank" | "review">("bank");
  const [publishStatusFilter, setPublishStatusFilter] = useState("");

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkMoveModal, setBulkMoveModal] = useState(false);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");

  if (!["admin", "master_admin", "moderator"].includes(user?.role ?? "")) { setLocation("/"); return null; }

  const effectiveView = isModerator ? "review" : pageView;

  const { data: appSettings } = useQuery<Record<string, string>>({
    queryKey: ["admin-settings"],
    queryFn: () => fetchWithAuth("/api/admin/settings"),
    enabled: isMaster,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (patch: Record<string, boolean | string>) =>
      fetchWithAuth("/api/admin/settings", { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["question-review-queue"] });
      toast({ title: "Review settings updated" });
    },
    onError: (e: unknown) =>
      toast({ title: "Failed to update settings", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const questionReviewEnabled = appSettings?.question_review_enabled === "true";
  const reviewersRequired = appSettings?.question_reviewers_required === "2" ? 2 : 1;

  const { data: bankStats } = useQuery<Record<string, number>>({
    queryKey: ["question-bank-stats"],
    queryFn: () => fetchWithAuth("/api/admin/questions/bank-stats"),
    refetchInterval: 15000,
  });

  const { data, isLoading } = useQuery<QuestionsResponse>({
    queryKey: ["admin-questions", activeTrack, search, categoryFilter, imageFilter, difficultyFilter, flaggedFilter, translatedFilter, idMin, idMax, publishStatusFilter, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: "50", track: activeTrack });
      if (search) params.set("search", search);
      if (categoryFilter) params.set("category", categoryFilter);
      if (imageFilter) params.set("hasImage", imageFilter);
      if (difficultyFilter) params.set("difficulty", difficultyFilter);
      if (flaggedFilter) params.set("flagged", flaggedFilter);
      if (translatedFilter) params.set("translated", translatedFilter);
      if (publishStatusFilter) params.set("publishStatus", publishStatusFilter);
      if (idMin) params.set("idMin", idMin);
      if (idMax) params.set("idMax", idMax);
      return fetchWithAuth(`/api/admin/questions?${params}`);
    },
    enabled: !isModerator && effectiveView === "bank",
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/api/admin/questions/${id}/publish`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-review-queue"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      toast({ title: "Question published" });
    },
    onError: (e: unknown) =>
      toast({ title: "Publish failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const resubmitMutation = useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/api/admin/questions/${id}/resubmit`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-review-queue"] });
      toast({ title: "Resubmitted for review" });
    },
    onError: (e: unknown) =>
      toast({ title: "Resubmit failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  // Fetch all categories from stats
  const { data: catStats } = useQuery<{ category: string }[]>({
    queryKey: ["category-stats"],
    queryFn: () => fetchWithAuth("/api/stats/categories"),
  });
  const allCategories = catStats?.map(c => c.category) ?? CATEGORIES;

  const createMutation = useMutation({
    mutationFn: (form: QuestionForm) =>
      fetchWithAuth("/api/admin/questions", {
        method: "POST",
        body: JSON.stringify({ ...serializeQuestionForm(form), licenseTrack: activeTrack }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-questions"] }); qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] }); setAddModal(false); toast({ title: "Question added" }); },
    onError: (e: unknown) => toast({ title: "Failed to add", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: QuestionForm }) =>
      fetchWithAuth(`/api/admin/questions/${id}`, { method: "PUT", body: JSON.stringify(serializeQuestionForm(form)) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-questions"] }); setEditQuestion(null); toast({ title: "Question updated" }); },
    onError: (e: unknown) => toast({ title: "Failed to update", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/api/admin/questions/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-questions"] }); qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] }); setDeleteQuestion(null); toast({ title: "Question deleted" }); },
    onError: (e: unknown) => toast({ title: "Failed to delete", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const translateMutation = useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/api/admin/questions/${id}/translate`, { method: "POST" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-questions"] }); toast({ title: "Translation complete", description: "French content saved." }); },
    onError: (e: unknown) => toast({ title: "Translation failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const batchTranslateMutation = useMutation({
    mutationFn: () => fetchWithAuth("/api/admin/questions/translate-batch", { method: "POST" }),
    onSuccess: (result: { queued: number }) => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      toast({
        title: result.queued === 0 ? "All questions already translated" : "Translation queued",
        description: result.queued > 0 ? `${result.queued} question${result.queued !== 1 ? "s" : ""} queued for FR translation. Refresh in a moment to see results.` : undefined,
      });
    },
    onError: (e: unknown) => toast({ title: "Batch translation failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const translateSelectedMutation = useMutation({
    mutationFn: (ids: number[]) => fetchWithAuth("/api/admin/questions/translate-selected", { method: "POST", body: JSON.stringify({ ids }) }),
    onSuccess: (result: { queued: number }) => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      toast({
        title: "Translation queued",
        description: `${result.queued} question${result.queued !== 1 ? "s" : ""} queued for FR translation. Results appear in a moment.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Translation failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const importMutation = useMutation({
    mutationFn: (questions: Array<Record<string, unknown>>) => fetchWithAuth("/api/admin/questions/import", {
      method: "POST",
      body: JSON.stringify({ questions: questions.map(q => ({ ...q, licenseTrack: (q as { licenseTrack?: string }).licenseTrack ?? activeTrack })) }),
    }),
    onSuccess: (result: { inserted: number; updatedFr: number; skipped: number; withFr: number; queued: number; publishStatus?: string }) => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-review-queue"] });
      qc.invalidateQueries({ queryKey: ["category-stats"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      setImportModal(false);
      const parts: string[] = [];
      if (result.inserted > 0) {
        const statusNote = result.publishStatus === "pending_review" ? " (pending review)" : result.publishStatus === "published" ? " (live)" : "";
        parts.push(`${result.inserted} new question${result.inserted !== 1 ? "s" : ""} added${statusNote}`);
      }
      if (result.updatedFr > 0) parts.push(`${result.updatedFr} existing question${result.updatedFr !== 1 ? "s" : ""} got FR translation`);
      if (result.skipped > 0) parts.push(`${result.skipped} duplicate${result.skipped !== 1 ? "s" : ""} skipped`);
      const frNote = result.withFr > 0 && result.queued === 0
        ? ` · All ${result.withFr} bilingual.`
        : result.withFr > 0
          ? ` · ${result.withFr} bilingual, ${result.queued} queued for translation.`
          : result.queued > 0
            ? ` · ${result.queued} queued for FR translation.`
            : "";
      toast({ title: "Import complete", description: parts.join(", ") + "." + frNote });
    },
    onError: (e: unknown) => toast({ title: "Import failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => fetchWithAuth("/api/admin/questions/bulk", { method: "DELETE", body: JSON.stringify({ ids }) }),
    onSuccess: (res: { deleted: number }) => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      setSelectionMode(false);
      toast({ title: "Deleted", description: `${res.deleted} question${res.deleted !== 1 ? "s" : ""} deleted.` });
    },
    onError: (e: unknown) => toast({ title: "Bulk delete failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, category, licenseTrack }: { ids: number[]; category: string | null; licenseTrack: string | null }) =>
      fetchWithAuth("/api/admin/questions/bulk-update", { method: "PATCH", body: JSON.stringify({ ids, category, licenseTrack }) }),
    onSuccess: (res: { updated: number }) => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      setSelectedIds(new Set());
      setBulkMoveModal(false);
      setSelectionMode(false);
      toast({ title: "Moved", description: `${res.updated} question${res.updated !== 1 ? "s" : ""} updated.` });
    },
    onError: (e: unknown) => toast({ title: "Bulk move failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => fetchWithAuth(`/api/admin/questions/all?track=${activeTrack}`, { method: "DELETE" }),
    onSuccess: (res: { deleted: number; track: string }) => {
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["question-bank-stats"] });
      qc.invalidateQueries({ queryKey: ["track-bank-counts"] });
      setDeleteAllConfirm(false);
      setDeleteAllConfirmText("");
      toast({ title: "All questions deleted", description: `${res.deleted} question${res.deleted !== 1 ? "s" : ""} removed from ${res.track.toUpperCase()}.` });
    },
    onError: (e: unknown) => toast({ title: "Delete all failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" }),
  });

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const pageIds = data?.questions.map(q => q.id) ?? [];
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const somePageSelected = pageIds.some(id => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.add(id)); return next; });
    }
  };

  const handleBulkDownloadJSON = () => {
    const selected = (data?.questions ?? []).filter(q => selectedIds.has(q.id));
    const payload = selected.map(q => ({
      id: q.id, text: q.text, options: q.options,
      correctOptionIndex: q.correctOptionIndex, explanation: q.explanation,
      category: q.category, difficulty: q.difficulty,
      referenceUrl: q.referenceUrl ?? null,
    }));
    triggerDownload(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }), `flight-deck-selection-${today()}.json`);
    toast({ title: "Downloaded", description: `${selected.length} questions exported as JSON.` });
  };

  const handleBulkDownloadCSV = () => {
    const selected = (data?.questions ?? []).filter(q => selectedIds.has(q.id));
    const esc = (v: string | null | undefined) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
    };
    const header = ["id","text","option_a","option_b","option_c","option_d","correct_option_index","explanation","category","difficulty","reference_url"];
    const rows = selected.map(q => [
      q.id, esc(q.text),
      esc(q.options[0]), esc(q.options[1]), esc(q.options[2]), esc(q.options[3]),
      q.correctOptionIndex, esc(q.explanation), esc(q.category), esc(q.difficulty),
      esc(q.referenceUrl),
    ].join(","));
    triggerDownload(new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" }), `flight-deck-selection-${today()}.csv`);
    toast({ title: "Downloaded", description: `${selected.length} questions exported as CSV.` });
  };

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  const [exporting, setExporting] = useState(false);
  const [exportDropdown, setExportDropdown] = useState(false);

  const fetchAllQuestions = async (): Promise<Question[]> => {
    const all: Question[] = [];
    let pg = 1;
    while (true) {
      const params = new URLSearchParams({ page: String(pg), limit: "200", track: activeTrack });
      const res: QuestionsResponse = await fetchWithAuth(`/api/admin/questions?${params}`);
      all.push(...res.questions);
      if (all.length >= res.total) break;
      pg++;
    }
    return all;
  };

  const handleExportCSV = async () => {
    setExportDropdown(false);
    setExporting(true);
    try {
      const all = await fetchAllQuestions();
      const escape = (v: string | null | undefined) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
      };
      const header = ["id","text","option_a","option_b","option_c","option_d","correct_option_index","explanation","category","difficulty","image_url","explanation_image_url","reference_url","created_at"];
      const rows = all.map(q => [
        q.id, escape(q.text),
        escape(q.options[0]), escape(q.options[1]), escape(q.options[2]), escape(q.options[3]),
        q.correctOptionIndex, escape(q.explanation),
        escape(q.category), escape(q.difficulty),
        escape(q.imageUrl), escape(q.explanationImageUrl),
        escape(q.referenceUrl),
        q.createdAt,
      ].join(","));
      const csv = [header.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      triggerDownload(blob, `flight-deck-questions-${new Date().toISOString().slice(0,10)}.csv`);
      toast({ title: "Export complete", description: `${all.length} questions exported to CSV.` });
    } catch (e: unknown) {
      toast({ title: "Export failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setExportDropdown(false);
    setExporting(true);
    try {
      const all = await fetchAllQuestions();
      const payload = all.map(q => ({
        id: q.id, text: q.text, options: q.options,
        correctOptionIndex: q.correctOptionIndex, explanation: q.explanation,
        category: q.category, difficulty: q.difficulty,
        imageUrl: q.imageUrl, explanationImageUrl: q.explanationImageUrl,
        referenceUrl: q.referenceUrl ?? null,
        createdAt: q.createdAt,
      }));
      triggerDownload(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }), `flight-deck-questions-${today()}.json`);
      toast({ title: "Export complete", description: `${all.length} questions exported to JSON.` });
    } catch (e: unknown) {
      toast({ title: "Export failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally { setExporting(false); }
  };

  const handleExportTSV = async () => {
    setExportDropdown(false);
    setExporting(true);
    try {
      const all = await fetchAllQuestions();
      const esc = (v: string | null | undefined) => (v ?? "").replace(/\t/g, " ").replace(/\n/g, " ").replace(/\r/g, "");
      const header = ["id","text","option_a","option_b","option_c","option_d","correct_option_index","explanation","category","difficulty","image_url","explanation_image_url","reference_url","created_at"];
      const rows = all.map(q => [
        q.id, esc(q.text),
        esc(q.options[0]), esc(q.options[1]), esc(q.options[2]), esc(q.options[3]),
        q.correctOptionIndex, esc(q.explanation), esc(q.category), esc(q.difficulty ?? ""),
        esc(q.imageUrl), esc(q.explanationImageUrl), esc(q.referenceUrl), q.createdAt,
      ].join("\t"));
      triggerDownload(new Blob([[header.join("\t"), ...rows].join("\n")], { type: "text/tab-separated-values;charset=utf-8;" }), `flight-deck-questions-${today()}.tsv`);
      toast({ title: "Export complete", description: `${all.length} questions exported to TSV.` });
    } catch (e: unknown) {
      toast({ title: "Export failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally { setExporting(false); }
  };

  const handleExportMarkdown = async () => {
    setExportDropdown(false);
    setExporting(true);
    try {
      const all = await fetchAllQuestions();
      const cell = (v: string | null | undefined) => (v ?? "").replace(/\|/g, "\\|").replace(/\n/g, " ");
      const lines = [
        "| # | Category | Difficulty | Question | A | B | C | D | Correct | Explanation | Reference |",
        "|---|----------|------------|----------|---|---|---|---|---------|-------------|-----------|",
        ...all.map((q, i) =>
          `| ${i + 1} | ${cell(q.category)} | ${cell(q.difficulty)} | ${cell(q.text)} | ${cell(q.options[0])} | ${cell(q.options[1])} | ${cell(q.options[2])} | ${cell(q.options[3])} | ${q.correctOptionIndex + 1} | ${cell(q.explanation)} | ${cell(q.referenceUrl)} |`
        ),
      ];
      triggerDownload(new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8;" }), `flight-deck-questions-${today()}.md`);
      toast({ title: "Export complete", description: `${all.length} questions exported to Markdown.` });
    } catch (e: unknown) {
      toast({ title: "Export failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally { setExporting(false); }
  };

  const handleExportXML = async () => {
    setExportDropdown(false);
    setExporting(true);
    try {
      const all = await fetchAllQuestions();
      const x = (v: string | null | undefined) => (v ?? "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      const lines = [
        '<?xml version="1.0" encoding="UTF-8"?>',
        "<questions>",
        ...all.map(q => [
          "  <question>",
          `    <id>${q.id}</id>`,
          `    <text>${x(q.text)}</text>`,
          `    <options>`,
          ...q.options.map((o, i) => `      <option index="${i}">${x(o)}</option>`),
          `    </options>`,
          `    <correctOptionIndex>${q.correctOptionIndex}</correctOptionIndex>`,
          `    <explanation>${x(q.explanation)}</explanation>`,
          `    <category>${x(q.category)}</category>`,
          `    <difficulty>${x(q.difficulty)}</difficulty>`,
          `    <imageUrl>${x(q.imageUrl)}</imageUrl>`,
          `    <explanationImageUrl>${x(q.explanationImageUrl)}</explanationImageUrl>`,
          `    <referenceUrl>${x(q.referenceUrl)}</referenceUrl>`,
          `    <createdAt>${q.createdAt}</createdAt>`,
          "  </question>",
        ].join("\n")),
        "</questions>",
      ];
      triggerDownload(new Blob([lines.join("\n")], { type: "application/xml;charset=utf-8;" }), `flight-deck-questions-${today()}.xml`);
      toast({ title: "Export complete", description: `${all.length} questions exported to XML.` });
    } catch (e: unknown) {
      toast({ title: "Export failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally { setExporting(false); }
  };

  const handleExportWord = async () => {
    setExportDropdown(false);
    setExporting(true);
    try {
      const all = await fetchAllQuestions();
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle } = await import("docx");

      const borderNone = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
      const noBorder = { top: borderNone, bottom: borderNone, left: borderNone, right: borderNone };

      const headerRow = new TableRow({
        tableHeader: true,
        children: ["#", "Category", "Difficulty", "Question", "A", "B", "C", "D", "✓", "Explanation"].map(h =>
          new TableCell({
            borders: noBorder,
            shading: { fill: "1a2a1a" },
            children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, color: "00cc00", size: 18 })] })],
          })
        ),
      });

      const dataRows = all.map((q, i) =>
        new TableRow({
          children: [
            String(i + 1), q.category, q.difficulty ?? "—",
            q.text, q.options[0], q.options[1], q.options[2], q.options[3],
            String(q.correctOptionIndex + 1), q.explanation,
          ].map(text =>
            new TableCell({
              borders: noBorder,
              children: [new Paragraph({ children: [new TextRun({ text: text ?? "", size: 16 })] })],
            })
          ),
        })
      );

      const doc = new Document({
        sections: [{
          children: [
            new Paragraph({
              heading: HeadingLevel.HEADING_1,
              children: [new TextRun({ text: "FLIGHT DECK — Question Bank", bold: true })],
            }),
            new Paragraph({
              children: [new TextRun({ text: `Exported ${new Date().toLocaleDateString("en-CA")} · ${all.length} questions`, size: 20, color: "666666" })],
            }),
            new Paragraph({ children: [] }),
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: [headerRow, ...dataRows],
            }),
          ],
        }],
      });

      const buffer = await Packer.toBlob(doc);
      triggerDownload(buffer, `flight-deck-questions-${today()}.docx`);
      toast({ title: "Export complete", description: `${all.length} questions exported to Word (.docx).` });
    } catch (e: unknown) {
      toast({ title: "Export failed", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally { setExporting(false); }
  };

  const today = () => new Date().toISOString().slice(0, 10);

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const difficultyBadge = (d: string | null) => {
    if (!d) return null;
    const color = d === "easy" ? "bg-primary/10 text-primary border-primary/20" : d === "hard" ? "bg-destructive/10 text-destructive border-destructive/20" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20";
    return <Badge className={cn("font-mono text-xs border", color)}>{d.toUpperCase()}</Badge>;
  };

  const publishStatusBadge = (status: string | undefined, approvals?: number) => {
    const s = status ?? "published";
    if (s === "published") {
      return (
        <Badge className="font-mono text-xs bg-primary/10 text-primary border-primary/20">
          Live
        </Badge>
      );
    }
    if (s === "pending_review") {
      return (
        <Badge className="font-mono text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
          Pending {approvals != null ? `(${approvals})` : ""}
        </Badge>
      );
    }
    if (s === "rejected") {
      return (
        <Badge className="font-mono text-xs bg-destructive/10 text-destructive border-destructive/30">
          Rejected
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="font-mono text-xs">
        {s}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {isModerator ? "Question Review" : "Question Bank"}
            </h1>
            <p className="text-muted-foreground font-mono text-sm mt-1">
              {TRACKS.find(t => t.key === activeTrack)?.fullName.toUpperCase()} &mdash;{" "}
              {effectiveView === "review" ? "REVIEW QUEUE" : `${data?.total ?? "..."} QUESTIONS`}
              {bankStats && (
                <span className="ml-3 text-muted-foreground/60">
                  ({BANK_COUNT_KEYS.reduce((a, k) => a + (Number(bankStats[k]) || 0), 0).toLocaleString()} total across all banks)
                </span>
              )}
            </p>
            {bankStats && (
              <div className="flex items-center gap-3 mt-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono">
                  <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                  <span className="text-green-400 font-bold">{Number(bankStats._translated ?? 0).toLocaleString()}</span>
                  <span className="text-muted-foreground">FR translated</span>
                </span>
                <span className="text-muted-foreground/40">·</span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  <span className="text-amber-400 font-bold">{Number(bankStats._untranslated ?? 0).toLocaleString()}</span>
                  <span className="text-muted-foreground">not yet translated</span>
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isModerator && (
              <>
                <Button
                  variant={effectiveView === "bank" ? "default" : "outline"}
                  onClick={() => setPageView("bank")}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Bank
                </Button>
                <Button
                  variant={effectiveView === "review" ? "default" : "outline"}
                  onClick={() => setPageView("review")}
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Review
                </Button>
              </>
            )}
            {effectiveView === "bank" && (
            <>
            <Button
              variant={selectionMode ? "default" : "outline"}
              onClick={() => { setSelectionMode(v => !v); setSelectedIds(new Set()); }}
            >
              <Layers className="w-4 h-4 mr-2" />
              {selectionMode ? "Cancel Select" : "Select"}
            </Button>
            <Button variant="outline" onClick={() => setImportModal(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button
              variant="outline"
              onClick={() => batchTranslateMutation.mutate()}
              disabled={batchTranslateMutation.isPending}
              className="gap-2 border-violet-500/40 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
              title="Translate all questions that don't yet have a French version"
            >
              <Languages className="w-4 h-4" />
              {batchTranslateMutation.isPending ? "Queuing…" : "Translate Untranslated"}
            </Button>
            <div className="relative">
              <div className="flex">
                <Button
                  variant="outline"
                  className="rounded-r-none border-r-0"
                  onClick={handleExportCSV}
                  disabled={exporting}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? "Exporting…" : "Export"}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-l-none px-2"
                  onClick={() => setExportDropdown(v => !v)}
                  disabled={exporting}
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </div>
              {exportDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setExportDropdown(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[170px]">
                    {[
                      { label: "Export as CSV", icon: Download, fn: handleExportCSV, desc: "spreadsheet" },
                      { label: "Export as JSON", icon: FileJson, fn: handleExportJSON, desc: "structured" },
                      { label: "Export as TSV", icon: Download, fn: handleExportTSV, desc: "tab-separated" },
                      { label: "Export as Markdown", icon: BookOpen, fn: handleExportMarkdown, desc: "table" },
                      { label: "Export as XML", icon: FileJson, fn: handleExportXML, desc: "LMS / quiz" },
                      { label: "Export as Word", icon: BookOpen, fn: handleExportWord, desc: ".docx" },
                    ].map(({ label, icon: Icon, fn, desc }) => (
                      <button
                        key={label}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                        onClick={fn}
                      >
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="flex-1">{label}</span>
                        <span className="text-[10px] text-muted-foreground/50 font-mono">{desc}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {user?.role === "master_admin" && (
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => { setDeleteAllConfirm(true); setDeleteAllConfirmText(""); }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All
              </Button>
            )}
            <Button onClick={() => setAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </Button>
            </>
            )}
          </div>
        </div>

        {isMaster && (
          <Card className="border-border bg-card">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-bold font-mono text-foreground tracking-wide">QUESTION REVIEW</span>
              </div>
              <label className="flex items-center justify-between gap-4 cursor-pointer select-none">
                <div>
                  <div className="text-sm font-medium text-foreground">Require review before questions go live</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {questionReviewEnabled
                      ? "New uploads from admins go to review. As master admin you can approve, reject, publish now, or publish your own uploads immediately."
                      : "Imports and new questions are published immediately (solo workflow)."}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    updateSettingsMutation.mutate({
                      question_review_enabled: !questionReviewEnabled,
                    })
                  }
                  disabled={updateSettingsMutation.isPending || !appSettings}
                  className={cn(
                    "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50",
                    questionReviewEnabled ? "bg-primary" : "bg-secondary",
                  )}
                  role="switch"
                  aria-checked={questionReviewEnabled}
                >
                  <span
                    className={cn(
                      "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transition-transform duration-200",
                      questionReviewEnabled ? "translate-x-5" : "translate-x-0",
                    )}
                  />
                </button>
              </label>
              {questionReviewEnabled && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={reviewersRequired === 1 ? "default" : "outline"}
                    onClick={() => updateSettingsMutation.mutate({ question_reviewers_required: "1" })}
                    disabled={updateSettingsMutation.isPending}
                  >
                    1 reviewer
                  </Button>
                  <Button
                    size="sm"
                    variant={reviewersRequired === 2 ? "default" : "outline"}
                    onClick={() => updateSettingsMutation.mutate({ question_reviewers_required: "2" })}
                    disabled={updateSettingsMutation.isPending}
                  >
                    2 reviewers
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Track Tabs */}
        <div className="flex border-b border-border">
          {TRACKS.map(t => (
            <button
              key={t.key}
              onClick={() => { setActiveTrack(t.key); setPage(1); setSearch(""); setCategoryFilter(""); }}
              className={cn(
                "flex items-center gap-2 px-5 py-3 text-sm font-mono tracking-wide transition-colors border-b-2 -mb-px",
                activeTrack === t.key
                  ? t.activeClass + " bg-background"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="font-bold">{t.label}</span>
              {bankStats?.[t.key] !== undefined && (
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded font-mono",
                  activeTrack === t.key ? "bg-primary/10" : "bg-muted text-muted-foreground"
                )}>
                  {bankStats[t.key].toLocaleString()}
                </span>
              )}
            </button>
          ))}
        </div>

        {effectiveView === "review" && user && (
          <QuestionReviewPanel
            activeTrack={activeTrack}
            userRole={user.role}
            userId={user.id}
          />
        )}

        {effectiveView === "bank" && (
        <>
        {/* Filters */}
        <div className="space-y-2">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search questions..."
                className="pl-9 bg-background"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
              className="h-10 rounded-md border border-border bg-background px-3 text-sm min-w-[160px]"
            >
              <option value="">All Categories</option>
              {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={imageFilter}
              onChange={e => { setImageFilter(e.target.value as "" | "yes" | "no"); setPage(1); }}
              className="h-10 rounded-md border border-border bg-background px-3 text-sm min-w-[150px]"
            >
              <option value="">All (images &amp; text)</option>
              <option value="yes">📷 Has Photo</option>
              <option value="no">No Photo</option>
            </select>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            <select
              value={difficultyFilter}
              onChange={e => { setDifficultyFilter(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm min-w-[140px]"
            >
              <option value="">All Difficulties</option>
              <option value="easy">🟢 Easy</option>
              <option value="medium">🟡 Medium</option>
              <option value="hard">🔴 Hard</option>
            </select>
            <select
              value={flaggedFilter}
              onChange={e => { setFlaggedFilter(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm min-w-[140px]"
            >
              <option value="">All Questions</option>
              <option value="true">🚩 Flagged</option>
              <option value="false">✅ Clean</option>
            </select>
            <select
              value={translatedFilter}
              onChange={e => { setTranslatedFilter(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm min-w-[155px]"
            >
              <option value="">All Translations</option>
              <option value="yes">🇫🇷 Translated</option>
              <option value="no">⏳ Untranslated</option>
            </select>
            <select
              value={publishStatusFilter}
              onChange={e => { setPublishStatusFilter(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm min-w-[150px]"
            >
              <option value="">All statuses</option>
              <option value="published">Live</option>
              <option value="pending_review">Pending review</option>
              <option value="rejected">Rejected</option>
            </select>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground font-mono">
              <span className="text-xs">ID#</span>
              <Input
                value={idMin}
                onChange={e => { setIdMin(e.target.value.replace(/\D/g, "")); setPage(1); }}
                placeholder="from"
                className="h-9 w-20 bg-background text-xs font-mono px-2"
              />
              <span className="text-xs">–</span>
              <Input
                value={idMax}
                onChange={e => { setIdMax(e.target.value.replace(/\D/g, "")); setPage(1); }}
                placeholder="to"
                className="h-9 w-20 bg-background text-xs font-mono px-2"
              />
            </div>
            {(difficultyFilter || flaggedFilter || translatedFilter || idMin || idMax) && (
              <button
                onClick={() => { setDifficultyFilter(""); setFlaggedFilter(""); setTranslatedFilter(""); setIdMin(""); setIdMax(""); setPage(1); }}
                className="text-xs font-mono text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Question List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : !data?.questions.length ? (
          <Card className="border-border">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground font-mono text-sm">No questions found.</p>
              <Button className="mt-4" onClick={() => setAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />Add First Question
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {selectionMode && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-secondary/40 border border-border rounded-md">
                <input
                  type="checkbox"
                  className="accent-primary w-4 h-4 shrink-0 cursor-pointer"
                  checked={allPageSelected}
                  ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                  onChange={toggleSelectAll}
                />
                <span className="text-xs font-mono text-muted-foreground">
                  {allPageSelected ? "Deselect all on this page" : "Select all on this page"} ({pageIds.length})
                </span>
                {selectedIds.size > 0 && (
                  <span className="ml-auto text-xs font-mono text-primary font-bold">{selectedIds.size} selected total</span>
                )}
              </div>
            )}
            {data.questions.map((q, idx) => {
              const isSelected = selectedIds.has(q.id);
              return (
                <div
                  key={q.id}
                  className={cn(
                    "group flex items-start gap-3 p-4 bg-card border rounded-md transition-colors",
                    selectionMode ? "cursor-pointer" : "hover:border-primary/30",
                    isSelected ? "border-primary/60 bg-primary/5" : "border-border",
                  )}
                  onClick={selectionMode ? () => toggleSelection(q.id) : undefined}
                >
                  {selectionMode && (
                    <input
                      type="checkbox"
                      className="accent-primary w-4 h-4 shrink-0 mt-0.5 cursor-pointer"
                      checked={isSelected}
                      onChange={() => toggleSelection(q.id)}
                      onClick={e => e.stopPropagation()}
                    />
                  )}
                  <span className="text-xs font-mono text-muted-foreground/50 w-8 shrink-0 pt-0.5 text-right">
                    {(page - 1) * 50 + idx + 1}.
                  </span>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <p className="text-sm text-foreground leading-snug line-clamp-2">{q.text}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="font-mono text-xs bg-secondary text-muted-foreground border-0">{q.category}</Badge>
                      {publishStatusBadge(q.publishStatus, q.reviewApprovals)}
                      {difficultyBadge(q.difficulty)}
                      {q.imageUrl && (
                        <span title="Has question image" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          <Camera className="w-3 h-3" />Q
                        </span>
                      )}
                      {q.explanationImageUrl && (
                        <span title="Has explanation image" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono bg-violet-500/10 text-violet-400 border border-violet-500/20">
                          <ImageIcon className="w-3 h-3" />E
                        </span>
                      )}
                      {q.translatedAt ? (
                        <span title="FR translation available" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          <Languages className="w-3 h-3" /> FR
                        </span>
                      ) : (
                        <span title="No French translation" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono text-muted-foreground/40 border border-border/30">
                          FR —
                        </span>
                      )}
                      {(q.openFlagCount ?? 0) > 0 && (
                        <button
                          title={`${q.openFlagCount} open flag${q.openFlagCount !== 1 ? "s" : ""} — click to review`}
                          onClick={async e => {
                            e.stopPropagation();
                            setFlagModal({ questionId: q.id, questionText: q.text });
                            setFlagDetailsLoading(true);
                            setFlagDetails([]);
                            const [res] = await Promise.all([
                              fetchWithAuth(`/api/admin/questions/${q.id}/flags`),
                              fetchWithAuth(`/api/admin/questions/${q.id}/flags/mark-seen`, { method: "POST" }),
                            ]);
                            setFlagDetails(res.flags ?? []);
                            setFlagDetailsLoading(false);
                            qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
                          }}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono bg-orange-500/15 text-orange-400 border border-orange-500/30 hover:bg-orange-500/25 transition-colors"
                        >
                          <Flag className="w-3 h-3" />
                          {q.openFlagCount}
                        </button>
                      )}
                      <span className="text-xs text-muted-foreground/50 font-mono">#{q.id}</span>
                    </div>
                    <p className="text-xs text-primary/70 font-mono">
                      ✓ {q.options[q.correctOptionIndex]?.slice(0, 60)}{q.options[q.correctOptionIndex]?.length > 60 ? "..." : ""}
                    </p>
                  </div>
                  {!selectionMode && (
                    <div className="shrink-0 flex flex-col gap-1 items-end opacity-0 group-hover:opacity-100 transition-opacity">
                      {isMaster && q.publishStatus === "pending_review" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => publishMutation.mutate(q.id)}
                        >
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Publish
                        </Button>
                      )}
                      {q.publishStatus === "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => resubmitMutation.mutate(q.id)}
                        >
                          Resubmit
                        </Button>
                      )}
                      <ActionMenu
                        onEdit={() => setEditQuestion(q)}
                        onDelete={() => setDeleteQuestion(q)}
                        onTranslate={() => translateMutation.mutate(q.id)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-mono text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Add Modal */}
      {addModal && (
        <QuestionModal
          initial={null}
          onClose={() => setAddModal(false)}
          onSave={form => createMutation.mutate(form)}
          saving={createMutation.isPending}
          categories={allCategories}
        />
      )}

      {/* Edit Modal */}
      {editQuestion && (
        <QuestionModal
          initial={questionFormFromApi(editQuestion)}
          onClose={() => setEditQuestion(null)}
          onSave={form => updateMutation.mutate({ id: editQuestion.id, form })}
          saving={updateMutation.isPending}
          categories={allCategories}
        />
      )}

      {/* Delete Confirmation */}
      {deleteQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteQuestion(null)} />
          <div className="relative z-10 bg-card border border-destructive/40 rounded-lg p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-destructive/20 p-2 rounded-md">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <h2 className="font-bold text-foreground font-mono">DELETE QUESTION</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              This will <span className="text-destructive font-semibold">permanently delete</span> this question and all associated answers. This cannot be undone.
            </p>
            <div className="bg-secondary/50 rounded-md p-3 mb-5 text-sm text-muted-foreground line-clamp-3">
              {deleteQuestion.text}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteQuestion(null)} disabled={deleteMutation.isPending}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-destructive hover:bg-destructive/90"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteQuestion.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirmation */}
      {deleteAllConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setDeleteAllConfirm(false); setDeleteAllConfirmText(""); }} />
          <div className="relative z-10 bg-card border-2 border-destructive/60 rounded-lg p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-destructive/20 p-2 rounded-md">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <h2 className="font-bold text-foreground font-mono tracking-wide">DELETE ALL QUESTIONS</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              This will <span className="text-destructive font-bold">permanently delete all {data?.total ?? "..."} questions</span> from the <span className="font-semibold">{activeTrack.toUpperCase()}</span> track, including all associated session answers. This <span className="font-semibold">cannot be undone</span>.
            </p>
            <p className="text-xs text-muted-foreground mb-4 font-mono">Type <span className="text-destructive font-bold">DELETE ALL</span> to confirm:</p>
            <input
              className="w-full bg-background border border-destructive/40 rounded-md px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-destructive mb-4"
              placeholder="DELETE ALL"
              value={deleteAllConfirmText}
              onChange={e => setDeleteAllConfirmText(e.target.value)}
              autoFocus
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setDeleteAllConfirm(false); setDeleteAllConfirmText(""); }}
                disabled={deleteAllMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                disabled={deleteAllConfirmText !== "DELETE ALL" || deleteAllMutation.isPending}
                onClick={() => deleteAllMutation.mutate()}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleteAllMutation.isPending ? "Deleting…" : "Delete All"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Detail Modal */}
      {flagModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setFlagModal(null)} />
          <div className="relative z-10 bg-card border border-orange-500/40 rounded-lg p-6 max-w-lg w-full shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center gap-3 mb-1 shrink-0">
              <div className="bg-orange-500/20 p-2 rounded-md">
                <Flag className="w-5 h-5 text-orange-400" />
              </div>
              <h2 className="font-bold text-foreground font-mono tracking-wide">FLAGS — Q#{flagModal.questionId}</h2>
              <button onClick={() => setFlagModal(null)} className="ml-auto text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-muted-foreground font-mono mb-4 line-clamp-2 shrink-0">{flagModal.questionText}</p>
            <div className="overflow-y-auto flex-1 space-y-3">
              {flagDetailsLoading ? (
                <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading flags…</div>
              ) : flagDetails.length === 0 ? (
                <div className="text-sm text-muted-foreground font-mono">No flags found.</div>
              ) : flagDetails.map((f: { id: number; user_name: string; user_email: string; note?: string; created_at: string; resolved_at?: string | null; resolved_by_name?: string | null }) => (
                <div key={f.id} className={cn("border rounded-md p-3 text-sm space-y-1", f.resolved_at ? "border-border/40 opacity-60" : "border-orange-500/30 bg-orange-500/5")}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-muted-foreground">
                      {f.user_name} ({f.user_email}) · {new Date(f.created_at).toLocaleString()}
                    </span>
                    {f.resolved_at ? (
                      <span className="text-xs font-mono text-green-400 flex items-center gap-1"><CheckCheck className="w-3 h-3" />Resolved</span>
                    ) : (
                      <button
                        className="text-xs font-mono text-green-400 hover:text-green-300 underline underline-offset-2"
                        onClick={async () => {
                          await fetchWithAuth(`/api/admin/questions/${flagModal.questionId}/flags/${f.id}/resolve`, { method: "PATCH" });
                          setFlagDetails(prev => prev.map(x => x.id === f.id ? { ...x, resolved_at: new Date().toISOString() } : x));
                          qc.invalidateQueries({ queryKey: ["admin-questions"] });
                        }}
                      >
                        Mark resolved
                      </button>
                    )}
                  </div>
                  <p className="text-foreground leading-snug">{f.note}</p>
                </div>
              ))}
            </div>
            <div className="shrink-0 mt-4 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditQuestion(data?.questions.find(q => q.id === flagModal.questionId) ?? null)}>
                <Edit className="w-3.5 h-3.5 mr-1.5" />
                Edit Question
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {importModal && (
        <ImportModal
          onClose={() => setImportModal(false)}
          onImport={qs => importMutation.mutate(qs)}
          importing={importMutation.isPending}
        />
      )}

      {/* Bulk Action Bar */}
      {selectionMode && selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-between gap-3 px-6 py-4 bg-card border-t border-border shadow-2xl animate-in slide-in-from-bottom-2 duration-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono font-bold text-foreground">{selectedIds.size} selected</span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-muted-foreground hover:text-foreground font-mono underline underline-offset-2"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-violet-500/40 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
              disabled={translateSelectedMutation.isPending}
              onClick={() => translateSelectedMutation.mutate(Array.from(selectedIds))}
            >
              <Languages className="w-4 h-4" />
              {translateSelectedMutation.isPending ? "Queuing…" : `Translate FR (${selectedIds.size})`}
            </Button>
            <div className="relative group/dl">
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="w-4 h-4" />
                Download
                <ChevronDown className="w-3 h-3" />
              </Button>
              <div className="absolute bottom-full right-0 mb-1 hidden group-hover/dl:block bg-popover border border-border rounded-md shadow-lg py-1 min-w-[150px] z-50">
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                  onClick={handleBulkDownloadJSON}
                >
                  <FileJson className="w-3.5 h-3.5 text-muted-foreground" />
                  Download as JSON
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                  onClick={handleBulkDownloadCSV}
                >
                  <Download className="w-3.5 h-3.5 text-muted-foreground" />
                  Download as CSV
                </button>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setBulkMoveModal(true)}>
              <FolderInput className="w-4 h-4" />
              Move
            </Button>
            <Button
              size="sm"
              className="gap-2 bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => setBulkDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4" />
              Delete {selectedIds.size}
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setBulkDeleteConfirm(false)} />
          <div className="relative z-10 bg-card border border-destructive/40 rounded-lg p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-destructive/20 p-2 rounded-md">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <h2 className="font-bold text-foreground font-mono">DELETE {selectedIds.size} QUESTIONS</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              This will <span className="text-destructive font-semibold">permanently delete</span> {selectedIds.size} question{selectedIds.size !== 1 ? "s" : ""} and all associated answers. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleteMutation.isPending}>Cancel</Button>
              <Button
                className="flex-1 bg-destructive hover:bg-destructive/90"
                disabled={bulkDeleteMutation.isPending}
                onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {bulkDeleteMutation.isPending ? "Deleting..." : `Delete ${selectedIds.size}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Move Modal */}
      {bulkMoveModal && (
        <BulkMoveModal
          count={selectedIds.size}
          allCategories={allCategories}
          currentTrack={activeTrack}
          onClose={() => setBulkMoveModal(false)}
          onApply={(category, licenseTrack) => bulkUpdateMutation.mutate({ ids: Array.from(selectedIds), category, licenseTrack })}
          applying={bulkUpdateMutation.isPending}
        />
      )}
    </Layout>
  );
}
